import { useMemo } from 'react';
import { Button, InlineLoading, Tag } from '@carbon/react';
import { Checkmark } from '@carbon/icons-react';
import { ElectionChart } from './election-chart';
import { fmtMonth, fmtUsd } from './interest-modeling-format';
import {
  buildElectionModel,
  electionGroupsOf,
  electionResultKey,
  isElectionItemComplete,
  type ElectionCard,
  type ElectionGroup,
  type ElectionVariant,
} from './interest-modeling-election';
import type { ElectionPreview } from './use-election-preview';
import type { InterestItem } from './types';
import styles from './election-results.module.scss';

export interface ElectionResultsProps {
  /** The live election preview session (per-tract results + status + actions). */
  preview: ElectionPreview;
  /** The election item the content area is focused on. */
  item: InterestItem;
  /**
   * Mark (or clear, with `null`) the outcome that feeds the evaluation FOR ONE GROUP.
   * A two-state toggle: picking a different outcome switches (≤1 per group — enforced
   * by the single `electedElection` field the parent writes on that group); clicking
   * the elected one again clears it.
   */
  onElect: (groupId: string, election: ElectionVariant | null) => void;
}

/**
 * The UMI election comparison surface: a SciChart comparison of the three elections'
 * cumulative net-to-owner curves + three outcome cards (NPV / undiscounted net / capital
 * at risk / payout) with the highest-NPV variant flagged, plus the "Use in Evaluation"
 * toggle and the feeds-the-evaluation callout. All reading the live per-tract
 * preview; the surface never blocks (a preparing / modeling state shows while the frame
 * builds, and a soft error keeps the last-good results).
 *
 * The elected outcome (`item.electedElection`) is pre-selected across a reopen (it rides
 * `configurations.interest_items`), so a committed eval that already modeled this UMI opens
 * with its path marked. Electing feeds the committed run natively (the calculator applies
 * the elected variant on Calculate) — no package-level delta is computed here. Marking one
 * outcome doesn't churn the preview (all three variants are always priced).
 *
 * @param {ElectionResultsProps} props - The preview session, the focused election item, and the elect handler.
 * @return {JSX.Element} The election comparison surface.
 */
export function ElectionResults({
  preview,
  item,
  onElect,
}: ElectionResultsProps) {
  const { status } = preview;
  const groups = useMemo(() => electionGroupsOf(item), [item]);
  // A group with no properties prices nothing — it is the state a fresh group opens
  // in, and showing it an empty-state panel would read as a failure rather than as
  // "nothing picked yet".
  const priced = groups.filter((group) => group.wellIds.length > 0);
  // "Out of date" is only meaningful OVER results. With nothing cached, each group's
  // own empty state carries the retry, and a second one up here would just be a
  // duplicate control saying the same thing.
  const anyResults = priced.some(
    (group) =>
      (preview.results.get(electionResultKey(item.tractId, group.id)) ?? [])
        .length > 0
  );

  if (priced.length === 0) {
    return (
      <div className={styles.state}>
        {renderEmptyState(status, item, () => preview.reprice(item))}
      </div>
    );
  }

  return (
    <div className={styles.groupResults}>
      {status === 'error' && anyResults && (
        <div className={styles.staleNote}>
          <span>Preview may be out of date.</span>
          <Button kind="ghost" size="sm" onClick={() => preview.reprice(item)}>
            Retry
          </Button>
        </div>
      )}
      {priced.map((group) => (
        <GroupResults
          key={group.id}
          preview={preview}
          item={item}
          group={group}
          showHeading={priced.length > 1}
          onElect={onElect}
        />
      ))}
    </div>
  );
}

/** Props for one group's comparison block. */
interface GroupResultsProps {
  preview: ElectionPreview;
  item: InterestItem;
  group: ElectionGroup;
  /** False for a single-group tract — the one heading would say nothing. */
  showHeading: boolean;
  onElect: (groupId: string, election: ElectionVariant | null) => void;
}

/**
 * One property group's comparison: its chart, its three outcome cards, and its own
 * elected-outcome callout. Each group elects independently — a tract can carry one
 * group and lease another — so the whole surface repeats per group.
 *
 * @param {GroupResultsProps} props - The preview session, the item, the group, and the elect handler.
 * @return {JSX.Element} The group's comparison block.
 */
function GroupResults({
  preview,
  item,
  group,
  showHeading,
  onElect,
}: GroupResultsProps) {
  const { results, status } = preview;
  const model = useMemo(
    () =>
      buildElectionModel(
        results.get(electionResultKey(item.tractId, group.id)) ?? []
      ),
    [results, item.tractId, group.id]
  );

  const elected = group.electedElection ?? null;
  const electedCard = elected
    ? (model.cards.find((card) => card.variant === elected) ?? null)
    : null;

  return (
    <section className={styles.groupBlock}>
      {showHeading && (
        <h4 className={styles.groupHeading}>
          {group.name}
          <span className={styles.groupHeadingCount}>
            {group.wellIds.length === 1
              ? '1 property'
              : `${group.wellIds.length} properties`}
          </span>
        </h4>
      )}

      {model.cards.length === 0 ? (
        <div className={styles.state}>
          {renderEmptyState(status, item, () => preview.reprice(item))}
        </div>
      ) : (
        <>
          <EvalCallout electedCard={electedCard} />

          <ElectionChart model={model} isLoading={status === 'preparing'} />

          <div className={styles.cards}>
            {model.cards.map((card) => (
              <OutcomeCard
                key={card.variant}
                card={card}
                isElected={card.variant === elected}
                onElect={(variant) => onElect(group.id, variant)}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

/**
 * The callout bar naming the outcome that will flow into the evaluation on the next
 * Calculate, or prompting for one when nothing is marked. The metrics restate the
 * elected outcome's own figures — this is not a package-level delta.
 *
 * @param {object} props - The callout props.
 * @param {ElectionCard | null} props.electedCard - The marked outcome's card, or null (none marked).
 * @return {JSX.Element} The callout bar.
 */
function EvalCallout({ electedCard }: { electedCard: ElectionCard | null }) {
  // One stable live region (aria-live) whose content swaps on elect, so a screen reader
  // announces the marked outcome; aria-pressed on the card toggle carries the two-state
  // semantics on its own.
  return (
    <div
      className={`${styles.evalBar} ${electedCard ? styles.evalBarActive : ''}`}
      aria-live="polite"
    >
      {electedCard ? (
        <>
          <span className={styles.evalBarLabel}>Feeds the evaluation</span>
          <span className={styles.evalBarValue}>{electedCard.label}</span>
          <span className={styles.evalBarMetrics}>
            NPV {fmtUsd(electedCard.npv)} · Undiscounted net{' '}
            {fmtUsd(electedCard.netToOwner)} · Payout{' '}
            {fmtMonth(electedCard.payoutMonth)}
          </span>
        </>
      ) : (
        <span className={styles.evalBarPrompt}>
          Select an outcome below to carry into the evaluation calculations.
        </span>
      )}
    </div>
  );
}

/**
 * One election's outcome card: the four metrics, the highest-NPV flag, and the
 * "Use in Evaluation" toggle.
 *
 * @param {object} props - The card props.
 * @param {ElectionCard} props.card - The variant's outcome metrics + best flag.
 * @param {boolean} props.isElected - Whether this outcome is marked to feed the evaluation.
 * @param {Function} props.onElect - Mark this outcome (its variant), or clear (`null`).
 * @return {JSX.Element} The outcome card.
 */
function OutcomeCard({
  card,
  isElected,
  onElect,
}: {
  card: ElectionCard;
  isElected: boolean;
  onElect: (election: ElectionVariant | null) => void;
}) {
  return (
    <div
      className={`${styles.card} ${card.isBest ? styles.cardBest : ''} ${
        isElected ? styles.cardElected : ''
      }`}
      style={{ borderTopColor: card.color }}
    >
      <div className={styles.cardHead}>
        <span className={styles.swatch} style={{ background: card.color }} />
        <span className={styles.cardTitle}>{card.label}</span>
        {card.isBest && (
          <Tag type="green" size="sm">
            Highest NPV
          </Tag>
        )}
      </div>
      <dl className={styles.metrics}>
        <div className={styles.metric}>
          <dt className={styles.metricLabel}>NPV (10%)</dt>
          <dd className={card.npv >= 0 ? styles.positive : styles.negative}>
            {fmtUsd(card.npv)}
          </dd>
        </div>
        <div className={styles.metric}>
          <dt className={styles.metricLabel}>Undiscounted net</dt>
          <dd>{fmtUsd(card.netToOwner)}</dd>
        </div>
        <div className={styles.metric}>
          <dt className={styles.metricLabel}>Capital at risk</dt>
          <dd>{fmtUsd(card.peakExposure)}</dd>
        </div>
        <div className={styles.metric}>
          <dt className={styles.metricLabel}>Payout</dt>
          <dd>{fmtMonth(card.payoutMonth)}</dd>
        </div>
      </dl>
      {/* A two-state toggle, not a one-way commit: re-clicking the elected outcome
        clears it. The label stays "Use in Evaluation" in BOTH states on purpose —
        marking an outcome only selects it; nothing is applied until the analyst runs
        Calculate, so a past-tense label would overstate it. The fill, the checkmark,
        and aria-pressed carry the selected state. */}
      <Button
        kind={isElected ? 'primary' : 'tertiary'}
        size="sm"
        className={styles.cardBtn}
        renderIcon={isElected ? Checkmark : undefined}
        aria-pressed={isElected}
        onClick={() => onElect(isElected ? null : card.variant)}
      >
        Use in Evaluation
      </Button>
    </div>
  );
}

// The non-blocking state shown while the tract has no election results yet.
function renderEmptyState(
  status: ElectionPreview['status'],
  item: InterestItem,
  onRetry: () => void
) {
  if (status === 'preparing') {
    return <InlineLoading description="Preparing preview…" status="active" />;
  }
  if (status === 'needs_recalculation') {
    // Old committed calc (predates the pairs sidecar) — no prepare will ever
    // complete, so a spinner here would never resolve. Same hint as the carry
    // surface.
    return (
      <span className={styles.stateHint}>
        Recalculate to preview interest modeling on this evaluation.
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className={styles.stateBody}>
        <span className={styles.stateHint}>
          Preview unavailable right now — it won’t block editing.
        </span>
        <Button kind="ghost" size="sm" onClick={onRetry}>
          Retry
        </Button>
      </span>
    );
  }
  if (isElectionItemComplete(item)) {
    return <InlineLoading description="Modeling elections…" status="active" />;
  }
  return (
    <span className={styles.stateHint}>
      Set the election terms to compare the three outcomes.
    </span>
  );
}
